import React, { useState, useEffect, useCallback } from 'react';

import ReactSelect from 'react-select';

import { FaCheck, FaTrash, FaPlus } from 'react-icons/fa';

import { toast } from 'react-hot-toast';
import { v4 } from 'uuid';

import api2 from '~/services/api2';
import apiIbge from '~/services/apiIbge';

import { useAuth } from '~/context/AuthContext';
import {
  IconTrashFilled,
  NotificationBanner,
  Switch,
} from 'takeat-design-system-ui-kit';
import { add } from 'date-fns';
import { BRLCurrencyInput } from '~/components/BRLCurrencyInput';
import {
  Card,
  AddressesTable,
  CheckButton,
  PlusButton,
  InputStyled,
  Header,
} from './styles';
import { CurrencyWrapper } from '../MapEditor/styles';

function Neighborhoods() {
  const { user } = useAuth();

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [currentState, setCurrentState] = useState(user.adress?.state || 'ES');

  const [itemIdLoading, setItemIdLoading] = useState(0);

  // Indica se algum dado foi alterado em caso positivo o botão é habilitado
  const [changed, setChanged] = useState([]);

  const customStyles = {
    control: (base) => ({
      ...base,
      height: 40,
      minHeight: 40,
      border: '1px solid #7a7a7a',
      borderRadius: 8,
    }),
  };

  const [addresses, setAddresses] = useState([]);

  const getAddresses = useCallback(async () => {
    try {
      const response = await api2.get('/restaurants/delivery-addresses');

      const allAddresses = response.data.map((addr) => ({
        key: v4(),
        ...addr,
      }));

      const state = user.adress?.state || 'ES';

      allAddresses.unshift({
        key: v4(),
        id: -1,
        country: 'BR',
        state,
        city: null,
        neighborhood: null,
        is_active: true,
        delivery_tax_price: null,
      });

      setAddresses(allAddresses);
      setChanged(new Array(allAddresses.length).fill(false));
    } catch (err) {
      toast.error('Erro ao carregar informações');
    }
  }, [user.adress]);

  useEffect(() => {
    getAddresses();
  }, [getAddresses]);

  const getStatesData = useCallback(async () => {
    try {
      const response = await apiIbge.get('/localidades/estados');
      const parsedStates = response.data.map((item) => {
        return {
          label: item.sigla,
          value: item.sigla,
        };
      });

      parsedStates.sort((a, b) => (a.value > b.value ? 1 : -1));
      setStates(parsedStates);
    } catch (err) {
      toast.error('Erro ao carregar informações');
    }
  }, []);

  const getCitiesData = useCallback(async () => {
    try {
      let data = [];

      // A api do IBGE não tem as cidades do DF, estas estão salvas no S3.
      if (currentState !== 'DF') {
        const response = await apiIbge.get(
          `/localidades/estados/${currentState}/municipios`
        );

        data = response.data;
      } else {
        const response = await api2.get(
          '/restaurants/delivery-addresses/cities'
        );

        data = response.data.DF.map((nome) => ({ nome }));
      }

      const parsedCities = data.map((item) => {
        return {
          label: item.nome,
          value: item.nome,
        };
      });

      parsedCities.sort((a, b) => (a.value > b.value ? 1 : -1));
      setCities(parsedCities);
      setItemIdLoading(0);
    } catch (err) {
      toast.error('Erro ao carregar informações');
    }
  }, [currentState]);

  useEffect(() => {
    getStatesData();
  }, [getStatesData]);

  useEffect(() => {
    getCitiesData();
  }, [getCitiesData]);

  // Mudança de estado do switch
  async function handleActiveChange(address, i) {
    address.is_active = !address.is_active;

    if (!changed[i]) {
      changed[i] = true;
      setChanged([...changed]);
    }

    setAddresses([...addresses]);
  }

  // Consulta troca o estado e reconsulta as cidades
  async function handleStateChange(value, address, i) {
    if (address.state !== value) {
      address.state = value;
      address.city = null;
      address.neighborhood = null;

      setItemIdLoading(address.id);
      setCurrentState(value);

      if (!changed[i]) {
        changed[i] = true;
        setChanged([...changed]);
      }

      setAddresses([...addresses]);
    }
  }

  async function handleCityChange(value, address, i) {
    console.log('@@ handleCityChange', value, address.city);
    if (address.city !== value) {
      address.city = value;

      if (!changed[i]) {
        changed[i] = true;
        setChanged([...changed]);
      }

      setAddresses([...addresses]);
    }
  }

  // Usado para buscar os bairros novamente se o estado foi alterado em algum momento
  async function handleCityOpen(address) {
    if (address.state !== currentState) {
      setItemIdLoading(address.id);
      setCurrentState(address.state);
    }
  }

  const handleNumberChange = useCallback(
    (e, i) => {
      addresses[i].delivery_tax_price = e;

      if (!changed[i]) {
        changed[i] = true;
        setChanged([...changed]);
      }
    },
    [addresses, changed]
  );

  const handleNeighborhoodChange = useCallback((e, i) => {
    const { value } = e.target;

    setAddresses((prev) =>
      prev.map((addr, idx) =>
        idx === i
          ? {
              ...addr,
              neighborhood: value || null, // se não existia, cria
            }
          : addr
      )
    );

    setChanged((prev) => prev.map((ch, idx) => (idx === i ? true : ch)));
  }, []);

  const handleTimeChange = useCallback(
    (e, i, address) => {
      address.delivery_time = e.target.value;

      if (!changed[i]) {
        changed[i] = true;
        setChanged([...changed]);
      }

      setAddresses([...addresses]);
    },
    [addresses, changed]
  );

  const saveEdited = useCallback(
    async (i) => {
      try {
        const address = addresses[i];
        if (!address) return;

        // normaliza neighborhood (null se vazio/undefined/whitespace)
        const normalizedNeighborhood =
          address.neighborhood && String(address.neighborhood).trim()
            ? String(address.neighborhood).trim()
            : null;

        // remove key, id e neighborhood antes de montar o payload
        const { key, id, neighborhood, ...rest } = address;

        const payload = {
          ...rest,
          is_neighborhood_limited: !!normalizedNeighborhood,
        };

        // adiciona neighborhood só se existir
        if (normalizedNeighborhood !== null) {
          payload.neighborhood = normalizedNeighborhood;
        }

        setItemIdLoading(id || 0);

        if (id === -1) {
          // criar
          const response = await api2.post(
            '/restaurants/delivery-addresses',
            payload
          );
          const newId = response.data?.id;

          setAddresses((prev) => {
            const updated = prev.map((a, idx) =>
              idx === i ? { ...a, id: newId } : a
            );

            const state = user.adress?.state || 'ES';
            const defaultAddress = {
              key: v4(),
              id: -1,
              country: 'BR',
              state,
              city: null,
              neighborhood: null,
              is_active: true,
              delivery_tax_price: null,
              delivery_time: null,
            };

            return [defaultAddress, ...updated];
          });

          setChanged((prev) => {
            const updated = prev.map((c, idx) => (idx === i ? false : c));
            return [false, ...updated];
          });
        } else {
          // atualizar
          await api2.put(`/restaurants/delivery-addresses/${id}`, payload);

          setChanged((prev) => prev.map((c, idx) => (idx === i ? false : c)));
        }

        setItemIdLoading(0);
        toast.success('Endereço salvo com sucesso!');
      } catch (e) {
        setItemIdLoading(0);
        console.error(e);
        toast.error('Erro ao salvar endereço');
      }
    },
    [addresses, user.adress]
  );

  const deleteAddress = useCallback(
    async (i) => {
      const address = addresses[i];

      if (address.id === -1) {
        return;
      }

      try {
        if (window.confirm('Tem certeza que deseja deletar este endereço?')) {
          await api2.delete(`/restaurants/delivery-addresses/${address.id}`);

          addresses.splice(i, 1);
          changed.splice(i, 1);

          setChanged([...changed]);
          setAddresses([...addresses]);

          toast.success('Endereço deletado com sucesso!');
        }
      } catch (err) {
        toast.error('Erro ao deletar endereço');
      }
    },
    [addresses, changed]
  );

  return (
    <Card>
      <Header>
        <NotificationBanner isOpen variant="warning">
          <p>
            {user.delivery_info.is_delivery_by_distance || user.delimit_by_area
              ? 'Este recurso é de extrema importância. O GPS pode ser impreciso em algumas regiões por problemas nos cadastros de CEP.'
              : 'Este recurso é de extrema importância. Cadastre ao menos 1 aréa de entrega'}
          </p>
        </NotificationBanner>
        <p
          style={{
            fontWeight: 500,
            fontSize: 14,
            color: '#222',
          }}
        >
          Informe regiões ou bairros onde seu estabelecimento realiza entrega.
        </p>
      </Header>
      <AddressesTable borderless>
        <thead>
          <tr>
            <th style={{ width: '50px' }}>Ativo</th>
            <th style={{ width: '100px' }}>UF</th>
            <th style={{ width: '200px' }}>Cidade</th>
            <th>Bairro</th>
            <th style={{ width: '130px' }}>Tx. de Entrega</th>
            <th style={{ width: '130px' }}>Tempo de Entrega</th>
            <th style={{ width: '80px' }}> </th>
          </tr>
        </thead>
        <tbody>
          {addresses.map((address, i) => (
            <tr key={address.key}>
              <td>
                {i > 0 && (
                  <Switch
                    id={`${address.id}`}
                    onChange={(value) => handleActiveChange(address, i)}
                    checked={address.is_active}
                  />
                )}
              </td>
              <td>
                <ReactSelect
                  classNamePrefix="react-select"
                  styles={customStyles}
                  placeholder="Selecione"
                  name="state"
                  options={states}
                  value={
                    address.state
                      ? { value: address.state, label: address.state }
                      : null
                  }
                  onChange={(e) => {
                    handleStateChange(e.value, address, i);
                  }}
                />
              </td>
              <td>
                <ReactSelect
                  classNamePrefix="react-select"
                  styles={customStyles}
                  placeholder="Selecione"
                  name="city"
                  options={cities}
                  value={
                    address.city
                      ? { value: address.city, label: address.city }
                      : null
                  }
                  onChange={(e) => {
                    handleCityChange(e.value, address, i);
                  }}
                  onMenuOpen={() => {
                    handleCityOpen(address);
                  }}
                  isLoading={address.id === itemIdLoading}
                />
              </td>
              <td>
                <InputStyled
                  name="neighborhood"
                  type="text"
                  placeholder="Esse campo vazio considera a cidade toda"
                  value={address.neighborhood}
                  onChange={(e) => handleNeighborhoodChange(e, i, address)}
                />
              </td>
              <td>
                <CurrencyWrapper>
                  <BRLCurrencyInput
                    hasPrefix
                    isCurrency
                    name="value"
                    value={address?.delivery_tax_price}
                    onChange={(e) => handleNumberChange(e.value, i)}
                  />
                </CurrencyWrapper>
              </td>
              <td>
                <InputStyled
                  name="delivery_time"
                  type="text"
                  placeholder="Ex: 20"
                  value={address?.delivery_time}
                  onChange={(e) => handleTimeChange(e, i, address)}
                />
              </td>
              {i > 0 ? (
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckButton
                      disabled={!changed[i]}
                      onClick={() => saveEdited(i)}
                    >
                      <FaCheck />
                    </CheckButton>
                    <IconTrashFilled
                      onClick={() => deleteAddress(i)}
                      fill="#C8131B"
                      style={{
                        fontSize: 24,
                        paddingLeft: 4,
                      }}
                    />
                  </div>
                </td>
              ) : (
                <td>
                  <PlusButton
                    disabled={!changed[i]}
                    onClick={() => saveEdited(i)}
                  >
                    <FaPlus />
                  </PlusButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </AddressesTable>
    </Card>
  );
}

export default Neighborhoods;
